import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * A hook that tracks page views based on location changes.
 * The tracking callback will fire once when the component mounts and whenever the location changes.
 *
 * @param trackingCallback - Function to call when a page view should be tracked
 * @param enabled - Whether tracking is enabled (default: true)
 *
 * @example
 * ```tsx
 * usePageViewTracking(() => {
 *   analytics.track('myPage', 'viewed', {
 *     Source: 'navigation',
 *     'User ID': userId,
 *   });
 * });
 * ```
 */
export function usePageViewTracking(trackingCallback: () => void, enabled = true) {
  const location = useLocation();
  const trackedLocationRef = useRef<string | null>(null);
  const callbackRef = useRef(trackingCallback);
  callbackRef.current = trackingCallback;

  useEffect(() => {
    if (!enabled) {
      return;
    }

    // Create a unique key for this location
    const locationKey = `${location.pathname}${location.search}`;

    // Only track if we haven't tracked this exact location yet
    if (trackedLocationRef.current !== locationKey) {
      callbackRef.current();
      trackedLocationRef.current = locationKey;
    }
  }, [location.pathname, location.search, enabled]);
}
